import * as THREE from 'three';
import { ball, box, material, mesh, random } from './art';
import type { Place } from './data';
import { picnicReserved } from './picnic';
import { cornersReserved } from './gardenCorners';
import { eveningReserved } from './eveningCafe';
import { DISTRICTS, districtAt } from './frontier';

export const GENERATION_VERSION = 1;
export const DEFAULT_SEED = 20260923;
export const PAINTINGS = [
  { id: 'mona-lisa', name: 'Mona Lisa', artist: 'Leonardo da Vinci', image: 'mona-lisa.jpg', x: 86, z: -27, width: 6.2, depth: 9.24, angle: -.16, frame: '#aa8450', story: 'A familiar, mysterious smile in a heavy gilt frame, left face-up on the courtyard stones.' },
  { id: 'starry-rhone', name: 'Starry Night Over the Rhone', artist: 'Vincent van Gogh', image: 'starry-rhone.jpg', x: -84, z: 59, width: 10, depth: 7.75, angle: .22, frame: '#716759', story: 'An entire night sky lies beside the cafe, as though someone set it down and forgot to return.' },
  { id: 'water-lilies', name: 'Water Lilies', artist: 'Claude Monet', image: 'water-lilies.jpg', x: -13, z: 53, width: 8.5, depth: 8.49, angle: -.3, frame: '#bba16a', story: 'A painted pond beside a real puddle. The wooden frame rests crookedly against the paving stones.' },
  { id: 'lady-ermine', name: 'Lady with an Ermine', artist: 'Leonardo da Vinci', image: 'lady-ermine.jpg', x: 168, z: 24, width: 6.2, depth: 8.3, angle: .17, frame: '#675644', story: 'A quiet portrait in a dark wooden frame, abandoned beside the garden path.' },
  { id: 'impression-sunrise', name: 'Impression, Sunrise', artist: 'Claude Monet', image: 'impression-sunrise.jpg', x: 312, z: 72, width: 10, depth: 7.76, angle: -.21, frame: '#ad8952', story: 'An orange sun rises from a canvas left on the picnic terrace. A little morning, waiting on the ground.' },
];
export const ARTWORKS = [
  ...PAINTINGS,
  { id: 'marble-muse', name: 'The marble muse', x: 99, z: -17, story: 'An original imaginary bust. Its stone pedestal feels like a cliff from down here.' },
  { id: 'bronze-loop', name: 'The endless ribbon', x: 123, z: -25, story: 'An original bronze sculpture: a circle with no beginning, framing a very small sky.' },
];
export function nearPainting(x: number, z: number, margin = 0): boolean {
  return PAINTINGS.some(p => Math.hypot(x - p.x, z - (p.z - 6)) < Math.hypot(p.width, p.depth) / 2 + 1 + margin);
}
export function paintingHeight(x: number, z: number): number {
  for (const p of PAINTINGS) {
    const dx = x - p.x; const dz = z - (p.z - 6);
    const lx = Math.abs(dx * Math.cos(p.angle) - dz * Math.sin(p.angle));
    const lz = Math.abs(dx * Math.sin(p.angle) + dz * Math.cos(p.angle));
    if (lx <= p.width / 2 + .6 && lz <= p.depth / 2 + .6) return lx > p.width / 2 || lz > p.depth / 2 ? .68 : .32;
  }
  return 0;
}
export const REGIONS: Place[] = [
  ...DISTRICTS,
  { id: 'evening-cafe', name: 'The evening cafe', subtitle: 'Warm lamps and a very sleepy gathering', x: 216, z: -20, color: '#b7a276', story: 'A quiet terrace under warm lamps. Follow the crumbs to three insects who have almost finished their imaginary tea.' },
  { id: 'fountain-edge', name: 'The fountain edge', subtitle: 'Turn the fallen spout to reveal the stepping stones', x: 216, z: 80, color: '#6ba6ad', story: 'A fountain becomes a sea. A fallen spout can quiet the water and uncover a stone crossing.' },
  { id: 'artist-corner', name: "The artist's corner", subtitle: 'Follow the little paint footprints', x: 312, z: 24, color: '#c96880', story: 'Someone left brushes and paint behind. Tiny colored footprints lead to a sketch of a familiar grumpy face.' },
  { id: 'checkered-picnic', name: 'The checkered picnic', subtitle: 'Push the berry aside to open the cloth pocket', x: 264, z: 72, color: '#bd7888', story: 'A checked blanket becomes a landscape. One push rolls the berry aside. A diamond ring waits inside the folded pocket.' },
  { id: 'tuileries', name: 'Tuileries Garden Path', subtitle: 'Beyond the courtyard', x: 168, z: 24, color: '#70915d', story: 'An imagined garden extension. Seed pods, stones and tall greenery line a wandering path.' },
  { id: 'clover', name: 'Clover wilderness', subtitle: 'A canopy of little giants', x: 264, z: -24, color: '#517b57', story: 'A quiet green clearing beyond the familiar city stones.' },
  { id: 'terraces', name: 'The picnic terraces', subtitle: 'Where the stone meets the meadow', x: 312, z: 72, color: '#b29b77', story: 'Warm pavement, scattered pebbles and a pocket-sized wilderness.' },
];
export interface GeneratedProp { x: number; z: number; radius: number; height: number; kind: 'stone' | 'plant'; }
export function generatedCell(seed: number, cx: number, cz: number): GeneratedProp[] {
  if (cx < 3 || cx > 6 || cz < -1 || cz > 1) return [];
  const r = random((seed ^ Math.imul(cx, 73856093) ^ Math.imul(cz, 19349663)) >>> 0);
  const result: GeneratedProp[] = [];
  for (let i = 0; i < 18; i++) {
    const lx = 5 + r() * 38; const lz = 5 + r() * 38;
    // All four edges join an unobstructed cross, including every discovery point.
    if (Math.abs(lx - 24) < 5 || Math.abs(lz - 24) < 5) continue;
    const prop: GeneratedProp = { x: cx * 48 + lx, z: cz * 48 + lz, radius: 1 + r() * 1.4, height: 3 + r() * 7, kind: r() < .3 ? 'stone' : 'plant' };
    // Reserve art footprints without changing the random sequence of other props.
    if (!nearPainting(prop.x, prop.z, 3) && !picnicReserved(prop.x, prop.z) && !cornersReserved(prop.x, prop.z) && !eveningReserved(prop.x, prop.z)) result.push(prop);
  }
  return result;
}
export function inWorld(x: number, z: number): boolean {
  return (x >= -132 && x <= 144 && z >= -70 && z <= 77) || (x >= 144 && x <= 336 && z >= -48 && z <= 96) || !!districtAt(x, z);
}

export function addGallery(parent: THREE.Group): { x: number; z: number; radius: number }[] {
  for (const p of PAINTINGS) {
    const g = new THREE.Group(); g.name = p.id; g.position.set(p.x, .08, p.z - 6); g.rotation.y = p.angle; parent.add(g);
    box(g, 0, .11, 0, p.width + 1.2, .22, p.depth + 1.2, '#60584c');
    const painting = new THREE.TextureLoader().load(`${import.meta.env.BASE_URL}assets/${p.image}`);
    painting.colorSpace = THREE.SRGBColorSpace; painting.anisotropy = 4;
    const canvas = mesh(new THREE.PlaneGeometry(p.width, p.depth), new THREE.MeshBasicMaterial({ map: painting }), g, 0, .24, 0);
    canvas.rotation.x = -Math.PI / 2; canvas.castShadow = false;
    for (const sign of [-1, 1]) {
      box(g, sign * (p.width / 2 + .3), .3, 0, .6, .6, p.depth + 1.2, p.frame);
      box(g, 0, .3, sign * (p.depth / 2 + .3), p.width, .6, .6, p.frame);
      box(g, sign * (p.width / 2 + .06), .39, 0, .1, .12, p.depth, '#d7bf82');
      box(g, 0, .39, sign * (p.depth / 2 + .06), p.width, .12, .1, '#d7bf82');
    }
  }
  for (const [x, z, bust] of [[99, -21, true], [123, -29, false]] as const) {
    box(parent, x, 1.4, z, 4, 2.8, 4, '#dddcd3');
    box(parent, x, 2.9, z, 4.5, .3, 4.5, '#f0eee3');
    if (bust) {
      ball(parent, x, 4.2, z, 1.8, 1.3, 1, '#e5e4dc');
      ball(parent, x, 6.3, z, 1.05, 1.5, 1, '#f0eee6');
      ball(parent, x, 6.2, z + .95, .2, .35, .3, '#e5e4dc');
      ball(parent, x, 7.2, z - .25, 1.12, .9, .95, '#c5c7bf');
    } else {
      const ribbon = mesh(new THREE.TorusKnotGeometry(2.5, .35, 72, 8), material('#739285', .35, .65), parent, x, 6, z); ribbon.rotation.y = .4;
    }
  }
  return [{ x: 99, z: -21, radius: 3 }, { x: 123, z: -29, radius: 3 }];
}

export class Outskirts {
  readonly cells = new Map<string, THREE.Group>();
  private readonly cache = new Map<string, GeneratedProp[]>();
  private readonly stone = new THREE.DodecahedronGeometry(1);
  private readonly stem = new THREE.CylinderGeometry(.12, .22, 1, 5);
  private readonly foliage = new THREE.SphereGeometry(1, 8, 5);
  private readonly ground = new THREE.PlaneGeometry(48, 48);
  private readonly path = new THREE.PlaneGeometry(48, 6);
  constructor(private parent: THREE.Group, readonly seed: number) {}
  private props(cx: number, cz: number): GeneratedProp[] {
    const key = `${cx}:${cz}`;
    if (!this.cache.has(key)) {
      if (this.cache.size >= 25) this.cache.delete(this.cache.keys().next().value!);
      this.cache.set(key, generatedCell(this.seed, cx, cz));
    }
    return this.cache.get(key)!;
  }
  blocked(x: number, z: number): boolean {
    return this.props(Math.floor(x / 48), Math.floor(z / 48)).some(p => Math.hypot(x - p.x, z - p.z) < p.radius + .34);
  }
  stream(x: number, z: number): void {
    const cx = Math.floor(x / 48); const cz = Math.floor(z / 48); const keep = new Set<string>();
    for (let a = Math.max(3, cx - 1); a <= Math.min(6, cx + 1); a++) for (let b = Math.max(-1, cz - 1); b <= Math.min(1, cz + 1); b++) {
      const key = `${a}:${b}`; keep.add(key); if (this.cells.has(key)) continue;
      const g = new THREE.Group(); const color = b === 1 ? '#c6c8b1' : b === -1 ? '#749668' : '#98aa78';
      const floor = mesh(this.ground, color, g, a * 48 + 24, -.025, b * 48 + 24); floor.rotation.x = -Math.PI / 2; floor.castShadow = false;
      for (const angle of [0, Math.PI / 2]) { const p = mesh(this.path, '#c6cbb4', g, a * 48 + 24, .005, b * 48 + 24); p.rotation.set(-Math.PI / 2, 0, angle); p.castShadow = false; }
      for (const p of this.props(a, b)) {
        if (p.kind === 'stone') { const m = mesh(this.stone, '#959d99', g, p.x, p.radius * .5, p.z); m.scale.set(p.radius, p.radius * .6, p.radius); }
        else {
          const s = mesh(this.stem, '#55785a', g, p.x, p.height / 2, p.z); s.scale.y = p.height;
          for (let i = 0; i < 3; i++) { const angle = i * Math.PI * 2 / 3; const l = mesh(this.foliage, b === -1 ? '#52805d' : '#779a58', g, p.x + Math.cos(angle) * p.radius * .5, p.height, p.z + Math.sin(angle) * p.radius * .5); l.scale.set(p.radius, .3, p.radius * .7); l.rotation.y = angle; }
        }
      }
      this.parent.add(g); this.cells.set(key, g);
    }
    // Geometry and materials are shared; removing groups releases only cell objects.
    for (const [key, g] of this.cells) if (!keep.has(key)) { this.parent.remove(g); this.cells.delete(key); }
  }
}
